import React, { useContext, useMemo } from 'react';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { ConfirmationModal, Table } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { API_DATE_FORMAT, API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useAdmins from '@ubeya/shared/hooks/account/useAdmins';
import { formatBalanceAmount } from './EmployeeCard/EmployeeLeaveManagementV2/leaveBalanceUtils';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.grey4};
  font-size: 14px;
  line-height: 20px;
`;

const Balance = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`;

const InfoBanner = styled.div`
  padding: 12px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.blueLight};
  color: ${({ theme }) => theme.colors.blue};
  font-size: 14px;
  line-height: 20px;
`;

const TableTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const TableWrapper = styled.div`
  max-height: 55vh;
  overflow: auto;
`;

const TableInner = styled.div`
  min-width: 760px;
  padding-block: 16px;
`;

const unbookModalContainerStyle = css`
  max-width: 920px !important;
  max-height: 90vh;
`;

const FooterNote = styled.div`
  color: ${({ theme }) => theme.colors.grey4};
  font-size: 13px;
  line-height: 18px;
`;

const parseDate = (value) => moment(value, [API_DATE_FORMAT, API_DATE_TIME_FORMAT, moment.ISO_8601]);
const parseTime = (value) => moment(value, [API_DATE_TIME_FORMAT, moment.ISO_8601, 'HH:mm:ss', 'HH:mm']);

const LeaveApprovalConfirmModal = ({
  variant = 'unbook',
  employeeName,
  firstName,
  leaveTypeName,
  dateRange,
  shifts = [],
  balance,
  expectedBalance,
  balanceUnit,
  onConfirm,
  onClose,
  loading = false,
  zIndex = 100
}) => {
  const { t, shortDateFormat, timeFormat } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();
  const { mappedBranches } = useBranches();
  const { mappedPositions } = usePositions();
  const { mappedAdmins } = useAdmins();

  const resolveBooker = useMemo(
    () => (bookerId) => {
      const booker = bookerId ? mappedAdmins?.[bookerId] : null;
      return booker ? [booker.firstName, booker.lastName].filter(Boolean).join(' ') : '';
    },
    [mappedAdmins]
  );

  const rows = useMemo(
    () =>
      shifts.map((shift, index) => {
        const dateMoment = parseDate(shift.date);
        const startMoment = parseTime(shift.startTime);
        const endMoment = parseTime(shift.endTime);

        const datePart = dateMoment.isValid() ? dateMoment.format(shortDateFormat) : '';
        const timePart = [
          startMoment.isValid() ? startMoment.format(timeFormat) : '',
          endMoment.isValid() ? endMoment.format(timeFormat) : ''
        ]
          .filter(Boolean)
          .join('–');

        return {
          id: shift.slotId ?? shift.shiftId ?? index,
          dateTime: [datePart, timePart].filter(Boolean).join(' · '),
          event: shift.projectName || '',
          position: shift.positionId ? mappedPositions?.[shift.positionId]?.slug || '' : '',
          bookedBy: resolveBooker(shift.bookerId),
          branch: shift.branchId ? mappedBranches?.[shift.branchId]?.name || '' : ''
        };
      }),
    [shifts, mappedBranches, mappedPositions, resolveBooker, shortDateFormat, timeFormat]
  );

  const branchCount = useMemo(() => new Set(shifts.map((shift) => shift.branchId).filter(Boolean)).size, [shifts]);

  const bookerNames = useMemo(() => {
    const names = shifts.map((shift) => resolveBooker(shift.bookerId)).filter(Boolean);
    return Array.from(new Set(names)).join(', ');
  }, [shifts, resolveBooker]);

  const columns = useMemo(
    () => [
      { Header: t('unbookConflicts_col_dateTime'), dataKey: 'dateTime', sortable: false },
      { Header: t('unbookConflicts_col_event'), dataKey: 'event', sortable: false },
      { Header: t('position'), dataKey: 'position', sortable: false },
      { Header: t('bookedBy'), dataKey: 'bookedBy', sortable: false },
      { Header: t('branch'), dataKey: 'branch', sortable: false }
    ],
    [t]
  );

  const isUnbook = variant === 'unbook';
  const hasBalance = balance !== null && balance !== undefined;
  const hasExpected = expectedBalance !== null && expectedBalance !== undefined;

  return (
    <ConfirmationModal
      isOpen
      usePortal
      isPermanentAction
      showCancelButton
      onClose={onClose}
      onConfirm={onConfirm}
      loadingConfirm={loading}
      zIndex={zIndex}
      confirmationModalContainerStyle={isUnbook ? unbookModalContainerStyle : undefined}
      title={
        isUnbook
          ? t('unbookConflicts_title', { employeeName, shiftCount: shifts.length })
          : t('createLeaveRecord_overdraw_title')
      }
      confirmText={isUnbook ? 'unbookConflicts_confirm' : 'createLeaveRecord_overdraw_confirm'}
      cancelText="cancel"
      warnings={isUnbook ? undefined : [t('createLeaveRecord_overdraw_warning')]}>
      <Content>
        {isUnbook ? (
          <Subtitle>
            {t('unbookConflicts_subtitle', {
              employeeName,
              leaveTypeName,
              dateRange,
              shiftCount: shifts.length,
              branchCount
            })}
          </Subtitle>
        ) : (
          <Subtitle>{t('createLeaveRecord_overdraw_body')}</Subtitle>
        )}

        {hasBalance && (
          <Balance>
            {t('unbookConflicts_currentBalance', {
              leaveTypeName,
              balance: formatBalanceAmount(balance, balanceUnit, t, formattingMap)
            })}
            {hasExpected && (
              <>
                {' '}
                ·{' '}
                {t('unbookConflicts_expectedBalance', {
                  balance: formatBalanceAmount(expectedBalance, balanceUnit, t, formattingMap)
                })}
              </>
            )}
          </Balance>
        )}

        {isUnbook && (
          <>
            <InfoBanner>{t('unbookConflicts_infoBanner', { firstName })}</InfoBanner>

            <TableTitle>{t('unbookConflicts_tableTitle')}</TableTitle>

            <TableWrapper>
              <TableInner>
                <Table
                  columns={columns}
                  data={rows}
                  autoHeight
                  headerRowHeight={50}
                  isVirtualized={false}
                  shadow={false}
                  sortable={false}
                />
              </TableInner>
            </TableWrapper>

            {!!bookerNames && <FooterNote>{t('unbookConflicts_footer', { bookers: bookerNames })}</FooterNote>}
          </>
        )}
      </Content>
    </ConfirmationModal>
  );
};

export default React.memo(LeaveApprovalConfirmModal);
